import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { FileText, ChevronDown, Clock, Stamp, ExternalLink, Lightbulb } from "lucide-react";

const bold = (s) =>
  s.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 ? <strong key={i} className="text-cream font-semibold">{part}</strong> : part));

const MarkdownLite = ({ text }) => {
  const blocks = [];
  let bullets = [];
  text.split("\n").forEach((line, i) => {
    const t = line.trim();
    if (/^[-•]\s/.test(t)) { bullets.push(t.replace(/^[-•]\s+/, "")); return; }
    if (bullets.length) { blocks.push({ type: "ul", items: bullets, key: `ul-${i}` }); bullets = []; }
    if (t) blocks.push({ type: "p", text: t, key: `p-${i}` });
  });
  if (bullets.length) blocks.push({ type: "ul", items: bullets, key: "ul-end" });
  return (
    <div className="flex flex-col gap-3 text-cream/85 leading-relaxed text-[15px]">
      {blocks.map((b) =>
        b.type === "ul" ? (
          <ul key={b.key} className="flex flex-col gap-1.5 pl-1">
            {b.items.map((it, j) => (
              <li key={j} className="flex gap-2.5"><span className="text-gold mt-[9px] w-1.5 h-1.5 bg-gold shrink-0" />{bold(it)}</li>
            ))}
          </ul>
        ) : (
          <p key={b.key}>{bold(b.text)}</p>
        )
      )}
    </div>
  );
};

const Meta = ({ label, value }) =>
  value ? (
    <div className="flex flex-col gap-1">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ash">{label}</span>
      <span className="text-sm text-cream">{value}</span>
    </div>
  ) : null;

const ExpiredStamp = () => (
  <span className="inline-flex items-center gap-1.5 border-2 border-gold text-gold font-mono text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 -rotate-2">
    <Stamp size={12} /> Expired · Practice brief
  </span>
);

const References = ({ refs }) => {
  if (!refs?.length) return null;
  if (typeof refs === "string") return <Meta label="References" value={refs} />;
  return (
    <div className="flex flex-col gap-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ash">Reference tracks</span>
      <ul className="flex flex-col gap-2">
        {refs.map((r, i) => {
          const sp = r.spotify?.find((s) => s.url);
          return (
            <li key={i} className="flex items-start gap-2 text-sm text-cream/90">
              <span className="text-gold mt-[8px] w-1.5 h-1.5 bg-gold shrink-0" />
              <span>
                {r.reference}
                {sp && (
                  <a href={sp.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 ml-2 text-gold hover:underline font-mono text-[11px] uppercase tracking-wide">
                    Spotify <ExternalLink size={11} />
                  </a>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const BriefBody = ({ brief }) => (
  <div className="flex flex-col gap-7">
    {(brief.project || brief.category) && (
      <div className="flex flex-wrap gap-2">
        {brief.category && <span className="border border-gold/50 text-gold px-3 py-1 font-mono text-[10px] uppercase tracking-wider">{brief.category}</span>}
        {brief.project && <span className="border border-line text-ash px-3 py-1 font-mono text-[10px] uppercase tracking-wider">{brief.project}</span>}
      </div>
    )}
    {(brief.brief || brief.body) && <MarkdownLite text={brief.brief || brief.body} />}
    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 border-t border-line pt-5">
      <Meta label="Genre / Style" value={brief.genre_style || brief.genre} />
      <Meta label="Tempo / Energy" value={brief.tempo_energy || brief.mood} />
      <Meta label="Vocals" value={brief.vocals} />
      <Meta label="Original deadline" value={brief.original_deadline} />
    </div>
    {brief.delivery_notes && (
      <div className="flex flex-col gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ash">Delivery notes</span>
        <MarkdownLite text={brief.delivery_notes} />
      </div>
    )}
    <References refs={brief.references} />
    {brief.coach_note && (
      <div className="border border-gold/40 bg-gold/5 p-5 flex gap-3">
        <Lightbulb size={18} className="text-gold shrink-0 mt-0.5" />
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold block mb-1.5">Coach's note</span>
          <p className="text-sm text-cream/90 leading-relaxed">{brief.coach_note}</p>
        </div>
      </div>
    )}
  </div>
);

function countdown(iso) {
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return "any moment now";
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  if (days > 0) return `${days} day${days === 1 ? "" : "s"}`;
  return `${hours} hour${hours === 1 ? "" : "s"}`;
}

export default function Briefs() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openWeek, setOpenWeek] = useState(null);

  useEffect(() => {
    api.get("/briefs")
      .then((r) => setData(r.data))
      .catch((e) => { if (e?.response?.status === 402) navigate("/paywall"); })
      .finally(() => setLoading(false));
  }, [navigate]);

  if (loading) return <div className="min-h-screen bg-ink flex items-center justify-center"><span className="font-mono text-xs uppercase tracking-[0.3em] text-ash animate-pulse">Loading…</span></div>;
  if (!data) return null;

  return (
    <div className="min-h-screen bg-ink">
      <Header />
      <div className="max-w-[860px] mx-auto px-5 md:px-10 pt-24 pb-24">
        <div className="mb-10">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">The Brief Machine</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter uppercase text-cream mt-3 leading-[0.95]">One brief.<br />Every week.</h1>
          <p className="text-ash mt-4 max-w-xl leading-relaxed">
            Real industry briefs from real pitches — their deadlines have passed, which makes them perfect practice.
            Write to them, build catalog, or grab a co-writer from the Haus and tackle this week's together.
          </p>
        </div>

        {data.total === 0 ? (
          <div data-testid="briefs-empty" className="border border-dashed border-line bg-surface p-10 text-center">
            <FileText size={26} className="mx-auto mb-4 text-gold" />
            <p className="font-heading text-xl font-bold uppercase text-cream mb-2">The machine is being stocked</p>
            <p className="text-ash text-sm">The first brief drops very soon. Check back shortly.</p>
          </div>
        ) : (
          <>
            {/* Current brief */}
            {data.current && (
              <div data-testid="current-brief" className="relative border border-line bg-surface overflow-hidden mb-10">
                <div className="absolute top-0 left-0 w-2 h-full bg-gold" aria-hidden />
                <div className="p-7 md:p-10">
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">This week · Brief {String(data.current.week).padStart(2, "0")} of {data.total}</span>
                    <ExpiredStamp />
                  </div>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter uppercase text-cream mb-6 leading-tight">{data.current.title || `Brief #${data.current.week}`}</h2>
                  <BriefBody brief={data.current} />
                </div>
              </div>
            )}

            {/* Next unlock */}
            {data.next_unlock_at && (
              <div data-testid="next-brief-countdown" className="flex items-center gap-3 border border-line bg-ink px-5 py-4 mb-12 font-mono text-xs uppercase tracking-[0.15em] text-ash">
                <Clock size={15} className="text-gold" />
                Next brief drops in {countdown(data.next_unlock_at)}
                {data.locked_count > 0 && <span className="ml-auto text-ash/60">{data.locked_count} still locked</span>}
              </div>
            )}

            {/* Archive */}
            {data.archive.length > 0 && (
              <div>
                <h3 className="font-mono text-xs uppercase tracking-[0.18em] text-ash mb-5">The archive — {data.archive.length} past brief{data.archive.length === 1 ? "" : "s"}</h3>
                <div className="flex flex-col gap-px bg-line border border-line">
                  {data.archive.map((b) => {
                    const open = openWeek === b.week;
                    return (
                      <div key={b.id || b.week} className="bg-surface">
                        <button
                          onClick={() => setOpenWeek(open ? null : b.week)}
                          data-testid={`archive-brief-${b.week}`}
                          className="w-full flex items-center gap-4 p-5 text-left hover:bg-ink/40 transition-colors"
                        >
                          <span className="font-mono text-[11px] text-gold w-14 shrink-0">WK {String(b.week).padStart(2, "0")}</span>
                          <span className="flex-1 font-heading text-lg font-bold uppercase tracking-tight text-cream truncate">{b.title || `Brief #${b.week}`}</span>
                          {(b.category || b.genre) && <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-wide text-ash">{b.category || b.genre}</span>}
                          <ChevronDown size={16} className={`text-ash transition-transform ${open ? "rotate-180" : ""}`} />
                        </button>
                        {open && <div className="px-5 pb-6 md:px-10 md:pb-8"><BriefBody brief={b} /></div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
